import React from 'react'
import styled from 'styled-components'
import theme from 'stories/utils/theme'
import Stack from 'components/elements/Stack'
import { SubjectWithTopics } from 'interfaces/Taxonomy'
import Link from 'next/link'
import EducationHatIcon from 'assets/icons/education_hat.svg'
import { formatPath, subjectsKnowsRoute, taxonomyLevelOne } from 'lib/constants/routes'

const Container = styled(Stack)`
    h2 {
        font-family: var(--font-inter);
        font-size: 24px;
        font-weight: 600;
        line-height: 32px;
        margin: 0;

        /* Reset */
        text-transform: none;

        @media (max-width: 750px) {
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
        }
    }
    h3 {
        font-family: var(--font-inter);
        font-size: 18px;
        font-weight: 500;
        line-height: 28px;
        color: ${theme.colors.deepSeaBlue};
        margin: 0;

        /* Reset */
        text-transform: none;

        @media (max-width: 750px) {
            font-size: 15px;
            line-height: 20px;
        }
    }
`

const TitleRow = styled.div<{ showHeadlineAsLinks: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    h2 {
        font-family: var(--font-inter);
        font-size: 24px;
        font-weight: 500;
        line-height: 32px;
        color: ${({ showHeadlineAsLinks }) => (showHeadlineAsLinks ? `${theme.colors.businessBlue}` : theme.colors.white)};
    }
    svg {
        position: relative;
        bottom: 1px;
        path {
            stroke: ${theme.colors.white};
        }
    }
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 50px;

    h3 {
        font-family: var(--font-inter);
        font-size: 18px;
        font-weight: 500;
        line-height: 28px;
        color: ${theme.colors.white};
        margin: 0;
    }

    @media (max-width: 1250px) {
        grid-template-columns: 1fr 1fr;
    }
    @media (max-width: 750px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`

const TaxonomyContainer = styled.div`
    display: grid;
    gap: 5px;
    width: 350px;
    cursor: pointer;
    svg path {
        stroke: ${theme.colors.deepSeaBlue};
    }
`

const HrefAnchor = styled.div`
    position: relative;
    bottom: 150px;
    height: 0px;
    width: 0px;
`

interface Props {
    subjectTopics: SubjectWithTopics
}

const SubjectTopics = ({ subjectTopics }: Props) => {
    const { topics, name, id } = subjectTopics

    const showHeadlineAsLinks = topics.length === 0

    return (
        <Container gutter={40}>
            <TitleRow showHeadlineAsLinks={showHeadlineAsLinks}>
                <HrefAnchor id={formatPath(name)} />
                <EducationHatIcon />
                {showHeadlineAsLinks ? (
                    <Link href={subjectsKnowsRoute(name, id)} key={id} prefetch={false}>
                        <h2>{name}</h2>
                    </Link>
                ) : (
                    <h2>{name}</h2>
                )}
            </TitleRow>
            {topics?.length ? (
                <Grid>
                    {topics.map((topic) => (
                        <Link href={taxonomyLevelOne(topic.slug)} key={topic.uuid}>
                            <TaxonomyContainer>
                                <h3>{topic.name}</h3>
                            </TaxonomyContainer>
                        </Link>
                    ))}
                </Grid>
            ) : null}
        </Container>
    )
}

export default SubjectTopics
